import {
  Compass,
  Database,
  ShieldCheck,
  Target,
  Workflow,
  Rocket,
} from "lucide-react";
import ServiceSectionShell from "./ServiceSectionShell";

const focusAreas = [
  {
    icon: Compass,
    tag: "Focus · 01",
    title: "Opportunity & ROI mapping",
    detail:
      "Identify and prioritize AI use cases across revenue cycle, care operations, and back-office workflows—with measurable KPI targets tied to business impact.",
  },
  {
    icon: Database,
    tag: "Focus · 02",
    title: "Data readiness & integration",
    detail:
      "Assess and prepare data pipelines across EHR, ERP, and RCM stacks—so AI models run on trustworthy, governed, and compliant inputs.",
  },
  {
    icon: ShieldCheck,
    tag: "Focus · 03",
    title: "Guardrails & change management",
    detail:
      "Operational runbooks, governance controls, and frontline enablement so AI gets adopted safely—without clinical or regulatory blowback.",
  },
];

const engagementMotion = [
  {
    icon: Target,
    stage: "01 · Assess",
    title: "Opportunity & data readiness",
    detail:
      "Two-week assessment maps AI use cases against your revenue, clinical, and operational priorities.",
  },
  {
    icon: Workflow,
    stage: "02 · Roadmap",
    title: "90-day deployment plan",
    detail:
      "Prioritized use cases with KPI targets, integration requirements, and risk controls written for healthcare environments.",
  },
  {
    icon: Rocket,
    stage: "03 · Deploy",
    title: "Production rollout with guardrails",
    detail:
      "AI in your EHR and RCM stack with runbooks, governance, and change management baked in on day one.",
  },
];

export default function AITransformationServices() {
  return (
    <ServiceSectionShell
      kicker="AI Transformation Services"
      title="Strategy-to-production AI for healthcare workflows"
      lead="Most healthcare AI stalls at the pilot stage. We help health systems ship AI where it changes throughput, cash flow, and risk—not dashboards that sit unused."
      tone="white"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-7">
        <div className="flex flex-col rounded-2xl border border-shark-200 bg-white p-6 sm:p-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-complement-600">
            What we deliver
          </p>
          <h4 className="mt-2 text-[1.1rem] font-bold text-shark-900">
            End-to-end AI transformation programs
          </h4>
          <ol className="mt-5 space-y-4">
            {focusAreas.map((a) => {
              const Icon = a.icon;
              return (
                <li key={a.tag} className="flex gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-complement-50 text-complement-700 ring-1 ring-complement-200">
                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                  <div>
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                      {a.tag}
                    </p>
                    <p className="mt-1 text-[0.95rem] font-bold text-shark-900">
                      {a.title}
                    </p>
                    <p className="mt-1 text-[0.85rem] leading-relaxed text-shark-500">
                      {a.detail}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="flex flex-col rounded-2xl border border-shark-200 bg-shark-50/60 p-6 sm:p-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-complement-600">
            Engagement motion
          </p>
          <h4 className="mt-2 text-[1.1rem] font-bold text-shark-900">
            From idea to production in three stages
          </h4>
          <ol className="mt-5 space-y-4">
            {engagementMotion.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.stage} className="flex gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-complement-600 ring-1 ring-shark-200">
                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                  <div>
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                      {s.stage}
                    </p>
                    <p className="mt-1 text-[0.95rem] font-bold text-shark-900">
                      {s.title}
                    </p>
                    <p className="mt-1 text-[0.85rem] leading-relaxed text-shark-500">
                      {s.detail}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </ServiceSectionShell>
  );
}
